import { ReactNode } from "react";

type ValueType = string | boolean;

type RadioInputOption<Type extends ValueType> = {
  name: string;
  subTitle?: string;
  children?: ReactNode;
  selectedContent?: ReactNode;
  value: Type;
  tag?: string;
  displayIcon?: string;
  dataTestId?: string;
  disabled?: boolean;
};

interface RadioInputProps<Type extends ValueType> {
  className?: string;
  alignment?: string;
  title: string;
  options: RadioInputOption<Type>[];
  changeValue: Type | null | undefined;
  changeFunction: (value: Type) => void;
}

/**
 * use the new RadioButton component instead in RadioButton.tsx
 */
export function RadioInput<Type extends ValueType>(
  props: RadioInputProps<Type>,
) {
  return (
    <div className="tw-cursor-pointer tw-pb-[3px] tw-pr-[13px] tw-pt-[5px]">
      {props.options.map((option, i) => {
        const optionId =
          props.title +
          option.name.toLocaleLowerCase().replaceAll(" ", "-") +
          option.value;
        return (
          <div key={i}>
            <div
              className={`${
                option.subTitle || props.alignment == "vertical"
                  ? "tw-mx-[8px] tw-my-[8px] tw-block"
                  : "tw-my-[3px] tw-mr-[3.25em] tw-inline-block"
              } tw-align-top ${props.className}`}
            >
              <label
                className="tw-cursor-pointer tw-text-black"
                htmlFor={optionId}
              >
                <div className="tw-flex tw-items-start">
                  {option.displayIcon ? (
                    <i className={option.displayIcon} />
                  ) : (
                    <input
                      type="radio"
                      id={optionId}
                      data-testid={
                        option.dataTestId ?? `${props.title}_${optionId}`
                      }
                      value={option.value.toString()}
                      name={props.title
                        .toLocaleLowerCase()
                        .replaceAll(" ", "-")}
                      checked={props.changeValue === option.value}
                      onChange={() => props.changeFunction(option.value)}
                      className="tw-float-left tw-cursor-pointer"
                      disabled={option.disabled}
                    />
                  )}
                  <div
                    className={`tw-ml-[10px] tw-inline-block ${
                      option.subTitle ? "tw-leading-[23px]" : ""
                    }`}
                  >
                    <span
                      className={
                        props.changeValue !== option.value
                          ? "tw-font-normal"
                          : ""
                      }
                    >
                      {option.name}
                      {option.tag ? (
                        <span className="tw-ml-[5px] tw-rounded-[4px] tw-bg-green-light-1 tw-p-[5px] tw-text-[12px] tw-font-normal tw-text-green-1">
                          {option.tag}
                        </span>
                      ) : null}
                    </span>
                    {option.subTitle ? (
                      <span className="tw-text-[12px] tw-font-normal tw-text-gray-3">
                        <br />
                        {option.subTitle}
                      </span>
                    ) : null}
                  </div>
                  {option.children}
                </div>
              </label>
              {option.selectedContent &&
                props.changeValue === option.value &&
                option.selectedContent}
            </div>
          </div>
        );
      })}
    </div>
  );
}
